import { getShowingPricesForVintageText } from '@helpers/vintage';
import { isPartnerWinery } from '@helpers/winery';
import { MerchantStatus, isVCMerchant, useI18N } from '@vivino/js-web-common';
import { RubyLibFullVintage, RubyLibMerchantCheckoutPrices } from '@webtypes/rubyLibApi';
import React, { useEffect, useState } from 'react';
import { fetchCheckoutPrices } from 'vivino-js/api/checkoutPrices';

import { isLinkoutMerchant } from '@lib/merchants';

import { BaseModal, ModalSize, POSITION_TOP } from '@components/BaseModal/BaseModal';

import Shop from './components/Shop.container';

import styles from './shopsModal.scss';

const TRANSLATIONS_PATH = 'components.shared.modals.shops_modal';
const TRANSLATIONS = {
  buyFromTrustedVivinoMechants: `${TRANSLATIONS_PATH}.buy_from_trusted_vivino_merchants`,
  onlineShops: `${TRANSLATIONS_PATH}.online_shops`,
  onlineShopsSellingThisWine: `${TRANSLATIONS_PATH}.online_shops_selling_this_wine`,
  vivinoCheckoutShops: `${TRANSLATIONS_PATH}.vivino_checkout_shops`,
};

interface GroupMerchantsReturnType {
  vc?: RubyLibMerchantCheckoutPrices[];
  nonVc: RubyLibMerchantCheckoutPrices[];
}

const groupMerchants = (
  merchantsWithPrices: RubyLibMerchantCheckoutPrices[]
): GroupMerchantsReturnType => {
  return merchantsWithPrices.reduce(
    (memo, merchantWithPrices) => {
      if (
        [
          MerchantStatus.Prospect,
          MerchantStatus.Basic,
          MerchantStatus.Preferred,
          MerchantStatus.VC,
          MerchantStatus.Linkout,
        ].includes(merchantWithPrices.merchant.status)
      ) {
        if (
          isVCMerchant(merchantWithPrices.merchant) ||
          isLinkoutMerchant(merchantWithPrices.merchant)
        ) {
          memo.vc.push(merchantWithPrices);
        } else {
          memo.nonVc.push(merchantWithPrices);
        }
      }
      return memo;
    },
    {
      vc: [],
      nonVc: [],
    }
  );
};

interface ShopsModalProps {
  vintage: RubyLibFullVintage;
  displayVcShops?: boolean;
  show: boolean;
  hidePPCLink?: boolean;
  onClose: () => void;
}

const ShopsModal = ({
  vintage,
  displayVcShops = false,
  onClose,
  show,
  hidePPCLink,
}: ShopsModalProps) => {
  const { t } = useI18N();
  const vintageId = vintage.id;
  const [merchants, setMerchants] = useState<RubyLibMerchantCheckoutPrices[]>([]);
  const [availabilityYear, setAvailabilityYear] = useState<number | string>(null);
  const groupedMerchants = groupMerchants(merchants);
  const isNonVcMerchantsPresent = groupedMerchants.nonVc.length > 0;
  const winery = vintage?.wine?.winery;

  useEffect(() => {
    if (show && (merchants.length === 0 || !availabilityYear)) {
      fetchCheckoutPrices({ vintageId }).then((data) => {
        setMerchants(data.prices.merchants_with_prices);
        setAvailabilityYear(data.prices.availability.vintage.year);
      });
    }
  }, [vintageId, show]);

  const showingPricesForVintageText = getShowingPricesForVintageText(
    availabilityYear,
    vintage.wine?.vintage_type
  );

  return (
    <BaseModal show={show} onClose={onClose} position={POSITION_TOP} size={ModalSize.Standard}>
      <div data-testid="shopsModal">
        {displayVcShops && groupedMerchants.vc.length > 0 && (
          <div className={styles.content}>
            <div className={styles.header}>
              <h2>{t(TRANSLATIONS.vivinoCheckoutShops)}</h2>
              <p className={styles.subHeading}>{t(TRANSLATIONS.buyFromTrustedVivinoMechants)}</p>
              <p>{showingPricesForVintageText}</p>
            </div>
            {groupedMerchants.vc.map(({ merchant, prices }) => (
              <Shop
                key={merchant.id}
                merchant={merchant}
                price={prices?.[0]}
                vintageId={vintageId}
                winery={winery}
              />
            ))}
          </div>
        )}
        {isNonVcMerchantsPresent && !hidePPCLink && (
          <div className={styles.content}>
            <div className={styles.header}>
              <h2>{t(TRANSLATIONS.onlineShops)}</h2>
              <p className={styles.subHeading}>{t(TRANSLATIONS.onlineShopsSellingThisWine)}</p>
              <p>{showingPricesForVintageText}</p>
            </div>
            {groupedMerchants.nonVc.map(({ merchant, prices }) => (
              <Shop
                key={merchant.id}
                merchant={merchant}
                price={prices?.[0]}
                vintageId={vintageId}
                winery={winery}
              />
            ))}
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default ShopsModal;
